import React from 'react';
import PropTypes from 'prop-types';
import EditableTitle from '../common/EditableTitle';

const CheckIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
    <path d="M2 5l2.2 2.2 3.8-3.8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PrintIcon = () => (
  <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 9V4h12v5M6 18H4a1 1 0 01-1-1v-6a1 1 0 011-1h16a1 1 0 011 1v6a1 1 0 01-1 1h-2m-12 0v4h12v-4m-12 0h12"
    />
  </svg>
);

const PROGRESS_PHASES = ['case_study', 'narration', 'goals', 'inner_monologue'];

const PHASE_TO_PROGRESS_LABEL = {
  case_study:      'Situation',
  narration:       'Conversation',
  goals:           'Goals',
  inner_monologue: 'Thoughts & Feelings',
};

// The 'situation' phase precedes case_study (before the case study conversation
// has produced anything yet) and still belongs on the first progress node.
// Any phase past inner_monologue (e.g. 'reporting') renders the whole track as done.
const getActivePhaseIndex = (currentPhase) => {
  if (currentPhase === 'situation') return 0;
  const idx = PROGRESS_PHASES.indexOf(currentPhase);
  return idx === -1 ? PROGRESS_PHASES.length : idx;
};

const ChatHeader = ({ conversation, currentPhase, onTitleUpdate }) => {
  const activePhase = getActivePhaseIndex(currentPhase);

  const getTypeBadgeClass = (type) => {
    if (type === 'sales') return 'sales';
    return 'leadership';
  };

  const getTypeLabel = (type) => {
    if (type === 'sales') return 'Sales';
    return 'Leadership';
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="chat-header">
      <div className="chat-header-left">
        {conversation.methodology !== 'basic' && (
          <span className={`type-badge ${getTypeBadgeClass(conversation.type)}`}>
            {getTypeLabel(conversation.type)}
          </span>
        )}
        <EditableTitle
          title={conversation.title}
          conversationId={conversation.id}
          onUpdate={onTitleUpdate}
        />
      </div>

      {currentPhase != null && (
        <div className="rp-progress-track">
          {PROGRESS_PHASES.map((phase, i) => {
            const done   = i < activePhase;
            const active = i === activePhase;
            const connectorWidth = done ? '100%' : '0%';

            return (
              <React.Fragment key={phase}>
                <div className="rp-phase-node">
                  <div className={`rp-phase-circle ${done ? 'done' : active ? 'active' : 'future'}`}>
                    {done   && <CheckIcon />}
                    {active && <div className="rp-phase-dot" />}
                  </div>
                  <span className={`rp-phase-label ${done ? 'done' : active ? 'active' : 'future'}`}>
                    {PHASE_TO_PROGRESS_LABEL[phase]}
                  </span>
                </div>

                {i < PROGRESS_PHASES.length - 1 && (
                  <div className="rp-connector">
                    <div className="rp-connector-fill" style={{ width: connectorWidth }} />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div className="chat-header-right">
        {currentPhase != null && currentPhase !== 'situation' && (
          <button
            className="rp-download-btn"
            onClick={handlePrint}
            title="Print / save as PDF"
          >
            <PrintIcon />
          </button>
        )}
      </div>
    </div>
  );
};

ChatHeader.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string,
    methodology: PropTypes.string
  }).isRequired,
  currentPhase: PropTypes.string,
  onTitleUpdate: PropTypes.func.isRequired
};

export default ChatHeader;